import React from "react";

/**
 * Top-level React ErrorBoundary. Any unhandled render/lifecycle error
 * below this boundary is caught and replaced with a calm, plain-language
 * recovery screen. This is separate from CRA's dev-mode error overlay
 * (which cannot be disabled in development but only appears locally);
 * this boundary is what production users would see.
 *
 * The boundary intentionally does not report the raw error text to
 * participants — it acknowledges the problem in plain language, tells
 * them their saved answers are safe, and offers a Reload button.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Best-effort: log the error to console so a developer looking at the
    // browser log can see it, but NEVER surface the raw error to the user.
    // We deliberately do not send this anywhere; adding an error-reporting
    // pipeline is a Phase-3 concern.
    // eslint-disable-next-line no-console
    console.error("[CRA3D] ErrorBoundary caught:", error, info);
  }

  handleReload = () => {
    // Preserve the auth token; simply reload the current route.
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-bg-base text-fg-primary flex items-center justify-center px-4 py-10"
      >
        <div className="max-w-lg w-full rounded-lg border border-border-subtle bg-bg-surface p-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-heading font-medium">
            Something went wrong on our side.
          </h1>
          <p className="mt-4 text-base text-fg-secondary leading-relaxed">
            Your saved answers are safe. Reload to continue where you left
            off. If you saw this while typing, everything up to your last
            "Saved" indicator is on the server.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-reload-btn"
              className="rounded-md bg-accent-warm text-bg-base font-medium h-10 px-6 hover:bg-accent-warm-hover"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              data-testid="error-home-btn"
              className="rounded-md border border-border-subtle text-fg-secondary hover:text-fg-primary font-medium h-10 px-6 hover:bg-bg-surface-hover"
            >
              Go to the landing page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
